import React from 'react';
import styled from 'styled-components';
import { Card, Icon, Tag } from '@repo/design-system';
import { ink, space, typeRole } from '@repo/design-tokens';
import type { AdminTwin, OfferedTwinType } from '@repo/frontend-types';
import { IconDisc } from '@repo/ui';

import AddressFields, { type AddressLine } from './AddressFields';
import type { TwinDetailsForm } from './twinDetailsForm';
import { isIconName } from './twinCreateForm';

/**
 * Twin details' left card (`twin-details-page-address`, R1–R3; frame 02): the folded identity line,
 * *Where is it?*, the country from a list and the five typed address lines.
 *
 * Frame 02 draws a postcode search box where the lines are; address search is parked (epic, Maria
 * 2026-10-15), so the lines are typed, built from the same `Field` and `Input` the step always had,
 * and the country is a searchable `Combobox` where the frame draws a select — both undrawn, for Rob
 * to ratify after the merge (spec Human Action 3). The country and lines are `AddressFields`, which
 * the summary's Address card renders too (`twin-details-page-summary`, R4.2).
 */

export type { AddressLine };

/** The twin's identity, as the folded line shows it. */
export type Identity = Pick<AdminTwin, 'name' | 'bid' | 'template' | 'twin_template_id'>;

/**
 * The identity folded to one line (`twin-details-page-create`, R6; frame 02's top card): the type's
 * disc, the name, the BID behind a lock, the type, and *Created*. The type's icon and name come from
 * the types table; when it cannot be read, or the twin's type is not one it offers, the line falls
 * back to the twin's own `template` word with no disc — and never shows an empty tag.
 */
export const IdentityLine: React.FC<{ identity: Identity; offered: OfferedTwinType[] | null }> = ({
  identity,
  offered,
}) => {
  const type = offered?.find(t => t.id === identity.twin_template_id) ?? null;
  const typeName = type?.name ?? (identity.template?.trim() || null);
  const icon = type && isIconName(type.icon) ? type.icon : null;
  return (
    <Card surface="on-glass" size="sm" className="identity">
      {icon && (
        <IconDisc tone="aqua" size={30}>
          <Icon name={icon} variant="solid" size="xs" />
        </IconDisc>
      )}
      <span className="identity-name">{identity.name}</span>
      <Tag
        surface="on-glass"
        tone="code"
        icon={<Icon name="lock" variant="solid" size="xs" />}
        aria-label={`BID ${identity.bid}, can't change`}
      >
        {identity.bid}
      </Tag>
      {typeName && (
        <Tag surface="on-glass" tone="neutral">
          {typeName}
        </Tag>
      )}
      <Tag
        surface="on-glass"
        tone="mint"
        className="identity-created"
        icon={<Icon name="check" variant="solid" size="xs" />}
      >
        Created
      </Tag>
    </Card>
  );
};

interface Props {
  className?: string;
  identity: Identity | null;
  offered: OfferedTwinType[] | null;
  /** The twin's saved country text — a code, a name, an alias, free text or blank. */
  storedCountry: string;
  form: TwinDetailsForm;
  onCountry: (code: string | null) => void;
  onLineChange: (field: AddressLine, value: string) => void;
  onLineBlur: (field: AddressLine) => void;
  onLineRestore: (field: AddressLine) => void;
}

const TwinDetailsWhere: React.FC<Props> = ({
  className,
  identity,
  offered,
  storedCountry,
  form,
  onCountry,
  onLineChange,
  onLineBlur,
  onLineRestore,
}) => {
  return (
    <Card surface="on-scene" size="lg" className={className}>
      {identity && <IdentityLine identity={identity} offered={offered} />}
      <h3 className="where-heading">Where is it?</h3>
      <AddressFields
        storedCountry={storedCountry}
        form={form}
        onCountry={onCountry}
        onLineChange={onLineChange}
        onLineBlur={onLineBlur}
        onLineRestore={onLineRestore}
      />
    </Card>
  );
};

export default styled(TwinDetailsWhere)`
  gap: ${space[4]};

  .where-heading {
    margin: ${space[2]} 0 0;
    font: ${typeRole.subtitle2.fontWeight} ${typeRole.subtitle2.fontSize} /
      ${typeRole.subtitle2.lineHeight} ${typeRole.subtitle2.fontFamily};
    color: ${ink[1]};
  }

  /* Frame 02's top card: one row, the name at 15 px semibold (the ramp's 16 px step, the nearest). */
  .identity {
    flex-direction: row;
    align-items: center;
    gap: ${space[3]};
  }

  .identity-name {
    font: ${typeRole.subtitle2.fontWeight} ${typeRole.subtitle2.fontSize} /
      ${typeRole.subtitle2.lineHeight} ${typeRole.subtitle2.fontFamily};
    color: ${ink[1]};
  }

  .identity-created {
    margin-left: auto;
  }
`;
